import React, { useEffect, useState, useMemo, use, useCallback } from 'react'
import ReactApexChart from 'react-apexcharts'
import ApexCharts from 'apexcharts'
import { IFTooltipIconsLoading, IFText, IFsvg } from 'Components'
import { Colors } from 'Theme'
import Styles from './ConnectorStatusChart.module.css'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import ConnectorStatusChartSkeleton from './ConnectorStatusChartSkeleton'
import RequestState from 'Enums/RequestState'
import { styled } from '@mui/system'

const GraphContainer = styled('div')(({ theme }) => ({
  '.apexcharts-legend': {
    display: 'grid !important',
    gridTemplateColumns: 'repeat(2, auto)',
    gap: `${2}px ${52}px`,
  },

  '.apexcharts-legend-series': {
    display: 'flex !important',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
}))

const ConnectorStatusChart = ({
  chartID = 'connectorUtilizationGraphChart',
  startTimestamp,
  endTimestamp,
  noData = false,
  fetchConnectorStatusRequestState,
  preparingSeriesData = [],
  isLive = false,
}) => {
  const computeDataBounds = () => {
    if (preparingSeriesData.length === 0) {
      return [null, null]
    }

    const starts = preparingSeriesData.map((e) =>
      new Date(e.startTime).getTime(),
    )
    const dataStart = Math.min(...starts)

    let dataEnd
    if (isLive) {
      if (preparingSeriesData.length === 1) {
        dataEnd = dataStart + 10 * 1000
      } else {
        const ends = preparingSeriesData.map((e) =>
          new Date(e.endTime || e.startTime).getTime(),
        )
        dataEnd = Math.max(...ends)
      }
    } else {
      dataEnd = endTimestamp
        ? new Date(endTimestamp).getTime()
        : Math.max(
            ...preparingSeriesData.map((e) =>
              new Date(e.endTime || e.startTime).getTime(),
            ),
          )
    }

    return [dataStart, dataEnd]
  }
  const [sessionStartMs, sessionEndMs] = useMemo(() => {
    return computeDataBounds()
  }, [preparingSeriesData, isLive, startTimestamp, endTimestamp])

  const totalDurationMinutes = useMemo(() => {
    if (preparingSeriesData.length === 0) return 1

    const firstTransferStartMs = new Date(
      preparingSeriesData[0].startTime,
    ).getTime()

    const lastTransfer = preparingSeriesData[preparingSeriesData.length - 1]

    let lastTransferEndMs

    if (isLive) {
      if (lastTransfer.endTime) {
        lastTransferEndMs = new Date(lastTransfer.endTime).getTime()
      } else {
        const startMs = new Date(lastTransfer.startTime).getTime()
        lastTransferEndMs = startMs + 10 * 1000
      }
    } else {
      lastTransferEndMs = new Date(
        lastTransfer.endTime ? lastTransfer.endTime : lastTransfer.updatedAt,
      ).getTime()
    }

    const durationMs = lastTransferEndMs - firstTransferStartMs
    const durationMinutes = Math.max(1, durationMs / 1000 / 60)

    return durationMinutes
  }, [preparingSeriesData, isLive])

  const { t } = useTranslation()

  const seriesColors = [
    Colors.ConnectorStatus.CHARGING,
    Colors.ConnectorStatus.FINISHING,
    Colors.ConnectorStatus.SuspendedEVSE,
    Colors.ConnectorStatus.FAULTED,
    Colors.ConnectorStatus.OCCUPIED,
    Colors.ConnectorStatus.PREPARING,
  ]
  const [series, setSeries] = useState([])
  const groupByStatus = useCallback(
    (status) => {
      const sorted = [...preparingSeriesData].sort(
        (a, b) => new Date(a.startTime) - new Date(b.startTime),
      )

      const enriched = sorted.map((entry, index) => {
        const next = sorted[index + 1]
        const startMs = new Date(entry.startTime).getTime()

        let syntheticEnd
        if (next) {
          syntheticEnd = new Date(next.startTime).getTime()
        } else if (entry.endTime) {
          const endMs = new Date(entry.endTime).getTime()
          syntheticEnd = Math.max(endMs, startMs)
        } else {
          syntheticEnd = startMs
        }

        return {
          ...entry,
          syntheticEnd,
        }
      })

      const filtered = enriched.filter((e) => e.connectorStatus === status)

      return filtered.map((e) => {
        let startMin =
          (new Date(e.startTime).getTime() - sessionStartMs) / 1000 / 60
        const endMin = (e.syntheticEnd - sessionStartMs) / 1000 / 60

        const isFirstOverall = e === enriched[0]
        if (isFirstOverall) startMin = 0

        return {
          x: 'Connector',
          y: [
            parseFloat(startMin),
            parseFloat(Math.max(endMin, startMin + 0.01)),
          ],
          rangeName: e.rangeName,
        }
      })
    },
    [preparingSeriesData, sessionStartMs, isLive, endTimestamp],
  )

  useEffect(() => {
    const updateSeries = () => {
      setSeries([
        { name: 'Charging', data: groupByStatus('Charging') },
        { name: 'Finishing', data: groupByStatus('Finishing') },
        { name: 'SuspendedEVSE', data: groupByStatus('SuspendedEVSE') },
        { name: 'Faulted', data: groupByStatus('Faulted') },
        { name: 'SuspendedEV', data: groupByStatus('SuspendedEV') },
        { name: 'Preparing', data: groupByStatus('Preparing') },
      ])
    }

    updateSeries()
  }, [preparingSeriesData, isLive])

  const stepForMins = (mins) => {
    if (mins <= 6) return 1
    if (mins <= 30) return 5
    if (mins <= 120) return 15
    return 60
  }

  const isNum = (n) => typeof n === 'number' && Number.isFinite(n)
  const fmt = (n) => (isNum(n) ? n.toFixed(3) : '')

  const customTooltip = ({ series, seriesIndex, dataPointIndex, w }) => {
    const sName = w.config.series?.[seriesIndex]?.name ?? ''
    const point = w.config.series?.[seriesIndex]?.data?.[dataPointIndex]
    let a, b
    if (point && typeof point === 'object') {
      if (Array.isArray(point.y)) {
        ;[a, b] = point.y
      } else if (Array.isArray(point)) {
        ;[a, b] = point
      } else if (isNum(point.y)) {
        a = point.y
      }
    } else if (Array.isArray(series?.[seriesIndex])) {
      a = series[seriesIndex][dataPointIndex]
    }
    const left = fmt(a)
    const right = fmt(b)
    const rangeText =
      left && right ? `${left} - ${right}` : left || right || '—'

    const key = (sName ?? '').toUpperCase()
    const color = Colors?.ConnectorStatus?.[key]
    return `
    <div class="${Styles.customTooltip}">
      <div class="${Styles.title}" style="color:${color};">
        ${sName}:
      </div>
      <div class="${Styles.range}">
        ${rangeText} ${t('ConnectorUtilization.Minutes')}
      </div>
    </div>
  `
  }

  const [options, setOptions] = useState({
    chart: {
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 1,
        animateGradually: {
          enabled: false,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      id: chartID,
      type: 'rangeBar',
      events: {
        beforeResetZoom: function (chartContext, opts) {
          return {
            xaxis: {
              min: startTimestamp,
              max: endTimestamp,
            },
          }
        },
      },
      selection: {
        enabled: true,
      },
      autoSelected: 'zoom',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      width: '600px',
    },
    states: {
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        rangeBarGroupRows: true,
        barHeight: '100%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: seriesColors,
    xaxis: {
      type: 'numeric',
      min: 0,
      max: totalDurationMinutes,

      title: { show: false },
      tickAmount: 6,
      labels: {
        offsetX: -10,
        formatter: (val) => {
          const step = stepForMins(totalDurationMinutes)
          const eps = 1
          const mod = val % step
          const near = mod < eps || step - mod < eps
          if (!near && val !== 0 && Math.abs(val - totalDurationMinutes) > eps)
            return ''

          if (val < 60) return `${val.toFixed(step >= 5 ? 0 : 1)} min`
          const h = Math.floor(val / 60)
          const m = Math.round(val % 60)
          return m ? `${h}h ${m}min` : `${h}h`
        },
      },
    },
    yaxis: {
      show: true,
      showAlways: true,
      floating: true,
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        show: true,
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      theme: 'dark',
      custom: customTooltip,
    },

    legend: {
      position: 'right',
      fontSize: '12px',
      fontWeight: '600',
      fontFamily: 'ProximaNova',
      horizontalAlign: 'center',
      offsetX: -58,
      markers: {
        width: 16,
        height: 16,
        radius: 2,
        shape: 'square',
      },

      formatter: function (seriesName) {
        return `‎ ${seriesName}`
      },
    },
  })

  const xAxisLabelFormatter = useCallback(
    (val) => {
      const step = stepForMins(totalDurationMinutes)
      const eps = 5
      const mod = val % step
      const near = mod < eps || step - mod < eps
      if (!near && val !== 0 && Math.abs(val - totalDurationMinutes) > eps)
        return ''

      if (val < 60) return `${val.toFixed(step >= 5 ? 0 : 1)} min`
      const h = Math.floor(val / 60)
      const m = Math.round(val % 60)
      return m ? `${h}h ${m}min` : `${h}h`
    },
    [totalDurationMinutes],
  )

  useEffect(() => {
    const step = stepForMins(totalDurationMinutes)
    const tickAmt = isLive
      ? Math.max(3, Math.min(12, Math.floor(totalDurationMinutes / step) + 1))
      : 4

    setOptions((prev) => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        min: 0,
        max: totalDurationMinutes,
        tickAmount: tickAmt,
        labels: {
          offsetX: -10,
          formatter: xAxisLabelFormatter,
        },
      },
    }))
  }, [totalDurationMinutes, isLive, xAxisLabelFormatter])
  const [enablePanning, setEnablePanning] = useState(false)
  const [cursor, setCursor] = useState('auto')

  useEffect(() => {
    if (fetchConnectorStatusRequestState !== RequestState.LOADING) {
      const chart = ApexCharts.getChartByID(chartID)
      chart.w.globals.panEnabled = enablePanning ? true : false
    }
  }, [enablePanning])

  const memoizedKeyDown = useMemo(
    () => (event) => {
      if (event.shiftKey && !enablePanning) {
        setCursor('grab')
        setEnablePanning(true)
      }
    },
    [enablePanning],
  )
  const memoizedKeyUp = useMemo(
    () => (event) => {
      if (event.key == 'Shift') {
        setCursor('auto')
        setEnablePanning(false)
      }
    },
    [],
  )

  useEffect(() => {
    window.addEventListener('keydown', memoizedKeyDown)
    window.addEventListener('keyup', memoizedKeyUp)
    return () => {
      window.removeEventListener('keydown', memoizedKeyDown)
      window.removeEventListener('keyup', memoizedKeyUp)
    }
  }, [])

  const resetButtonOnClick = () => {
    const chart = ApexCharts.getChartByID(chartID)
    chart.resetSeries()
  }

  return (
    <div id="chart" className={Styles.WrapperContainer}>
      {fetchConnectorStatusRequestState === RequestState.LOADING ? (
        <>
          <ConnectorStatusChartSkeleton />
        </>
      ) : (
        <>
          <div className={Styles.MapHeader}>
            <IFText
              className={Styles.StatisticsSubTitle}
              style={{ color: Colors.text }}
            >
              {t('TransactionEvents.StatusOverTime')}
            </IFText>
            <div className={Styles.ResetButton}>
              <IFTooltipIconsLoading
                onClick={() => (noData ? null : resetButtonOnClick())}
                tooltipPlacement={'left'}
                title={t('ConnectorUtilization.ResetButtonText')}
                Icon={IFsvg.Undo}
                FilledIcon={IFsvg.Undo}
                isLoading={false}
                size={26}
              />
            </div>
          </div>
          <div className={Styles.GraphContainer} style={{ cursor: cursor }}>
            <GraphContainer>
              <ReactApexChart
                options={options}
                series={series}
                type="rangeBar"
                height={95}
                width={935}
                className={noData ? Styles.OverlayTest : {}}
              />
            </GraphContainer>
            {noData ? (
              <IFText
                className={Styles.OverlayText}
                style={{ color: Colors.red }}
              >
                {t('ConnectorUtilization.NoData')}
              </IFText>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

ConnectorStatusChart.propTypes = {
  chartID: PropTypes.string,
  startTimestamp: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
  ]),
  endTimestamp: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
  ]),
  noData: PropTypes.bool,
  fetchConnectorStatusRequestState: PropTypes.string,
  preparingSeriesData: PropTypes.arrayOf(
    PropTypes.shape({
      connectorStatus: PropTypes.string.isRequired,
      startTime: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
    }),
  ),
}

export default ConnectorStatusChart
